import type { BulkDeliveryResponse } from "~/entities/BulkDeliveryResponse";
import type { Delivery, DeliveryDM, DeliveryEmail } from "~/entities/Delivery";
import type { StepDeliveryResponse } from "~/entities/StepDeliveryResponse";
import type { MessageType } from "~/entities/Talk";
import type { UserTagType } from "~/entities/UserTagType";
import type { UserTagTypeResponse } from "~/entities/UserTagTypeResponse";

export type DeliveryType = "BULK" | "STEP";
export type DeliveryStatus = "RUNNING" | "SENDING" | "STOP" | "DRAFT" | "SENT" | "ABORTED";

export const getDeliveryStatus = (status: DeliveryStatus, type: DeliveryType = "BULK"): string => {
  switch (status) {
    case "RUNNING":
      return type === "BULK" ? "配信準備中" : "稼働中";
    case "SENDING":
      return type === "BULK" ? "処理中" : "稼働中";
    case "STOP":
      return "停止中";
    case "DRAFT":
      return "下書き";
    case "SENT":
      return "配信済み";
    case "ABORTED":
      return "配信エラー";
    default:
      return "不明なエラー";
  }
};

export interface IDeliveryTestDeliveryBody {
  body: {
    json: string;
    html: string;
  };
  plan_id: string;
  sender_name: string;
  subject: string;
  to: string;
}

export interface IDeliverySaveBodyStep {
  deliveryDaysLater: number;
  deliveryTime: number;
  dmContents: DeliveryDM;
  mailContents: DeliveryEmail;
  isSendMail: boolean;
  isSendDm: boolean;
  stepName: string;
  stepOrder: number;
  sendCount: number;
  isInstantDeliveryStep?: boolean;
  created?: boolean;
}

export interface IDeliverySaveBodyCondition {
  userTagConditions: UserTagType[];
  isExistUserTag: boolean;
  isAndUserTag: boolean;
  isAndCondition: boolean;
}

export interface IDeliverySaveTalkBody {
  talk_type: MessageType;
  contents: string;
  thumbnail_image: string;
  file_name: string;
  note: string;
  file_key: string;
  file_url: string;
}

export interface IDeliverySaveBody {
  name: string;
  delivery_type: DeliveryType;
  is_immediate_delivery: boolean;
  schedule_date_time: string;
  is_send_dm: boolean;
  is_send_mail: boolean;
  mail_contents: {
    subject: string;
    sender_name: string;
    body: {
      json: string;
      html: string;
    };
  };
  dm_contents: {
    messages: IDeliverySaveTalkBody[];
    send_source_user_id: string;
    send_dm_plan_id: string;
  };
  status: string;
  condition: {
    conditions: {
      user_tag_conditions: {
        user_tag_type_id: number;
        user_tag_category: string;
        user_tag_name: string;
        user_tag_key1: string;
        user_tag_key2: string;
        user_tag_key3: string;
      }[];
      is_exist_user_tag: boolean;
      is_and_user_tag: boolean;
      is_and_condition: boolean;
    }[];
  };
  step_trigger: {
    user_tag_type_id: number;
    user_tag_category: string;
    user_tag_name: string;
    user_tag_key1: string;
    user_tag_key2: string;
    user_tag_key3: string;
  }[];
  steps: {
    step_name: string;
    step_order: number;
    delivery_days_later: number;
    delivery_time: number;
    is_send_mail: boolean;
    is_send_dm: boolean;
    is_instant_delivery_step?: boolean;
    mail_contents: {
      subject: string;
      sender_name: string;
      body: {
        json: string;
        html: string;
      };
    };
    dm_contents: {
      messages: IDeliverySaveTalkBody[];
      send_source_user_id: string;
      send_dm_plan_id: string;
    };
  }[];
}

export interface IDeliveryRepository {
  // 一斉配信一覧取得
  getBulkDeliveries(options?: Record<string, any>): Promise<BulkDeliveryResponse>;

  // ステップ配信一覧取得
  getStepDeliveries(options?: Record<string, any>): Promise<StepDeliveryResponse>;

  // 一斉配信詳細取得
  getDelivery(deliveryId: string, options?: Record<string, any>): Promise<Delivery>;

  // change status
  changeDeliveryStatus(deliveryId: string, status: DeliveryStatus): Promise<void>;

  // 一斉配信の追加
  createDelivery(body: IDeliverySaveBody): Promise<void>;

  // 一斉配信の更新
  updateDelivery(deliveryId: string, body: IDeliverySaveBody): Promise<void>;

  // 一斉配信の削除
  deleteDelivery(deliveryId: string): Promise<void>;

  getUserCount(conditions: IDeliverySaveBodyCondition[]): Promise<{ userCount: number }>;

  getAccountPlans(): Promise<{ planId: string; planTitle: string }[]>;

  getMailTemplates(): Promise<DeliveryEmail[]>;

  getTagTypes(): Promise<UserTagTypeResponse>;

  testDelivery(body: IDeliveryTestDeliveryBody): Promise<void>;
}
